import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import GlassCard from '../../components/GlassCard';
import AboutProps from '../../model/about-props';
import CodeIcon from '@mui/icons-material/Code';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';

const services = [
  {
    icon: <DesignServicesIcon sx={{ fontSize: 40 }} />,
    title: 'Design',
    description: 'I value simple content structure, clean design patterns, and thoughtful interactions.',
    projects: 4,
    color: '#44D482',
  },
  {
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    title: 'Front End',
    description: 'I enjoy building things from the ground up and bringing them to life.',
    projects: 9,
    color: '#6EEAA3',
  },
  {
    icon: <StorageIcon sx={{ fontSize: 40 }} />,
    title: 'Back End',
    description: 'I enjoy building secured, documented, optimized APIs, Microservice and server utilities.',
    projects: 4,
    color: '#44D482',
  },
  {
    icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
    title: 'Machine Learning',
    description: 'Building intelligent and adaptive systems that learn and evolve.',
    projects: 2,
    color: '#6EEAA3',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export default function About({ isMobile }: AboutProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <Box
      component="section"
      id="about"
      ref={ref}
      sx={{
        py: { xs: 10, md: 15 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {/* Left side - Service cards */}
          <Grid item xs={12} md={6}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
            >
              <Grid container spacing={3}>
                {services.map((service) => (
                  <Grid item xs={12} sm={6} key={service.title}>
                    <motion.div variants={itemVariants}>
                      <GlassCard
                        enableTilt={!isMobile}
                        sx={{
                          height: '100%',
                          minHeight: '220px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box
                          sx={{
                            color: service.color,
                            mb: 2,
                          }}
                        >
                          {service.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: '#fff',
                          }}
                        >
                          {service.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.9rem',
                            lineHeight: 1.6,
                            flex: 1,
                          }}
                        >
                          {service.description}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Fira Code, monospace',
                            fontSize: '0.75rem',
                            color: service.color,
                            mt: 2,
                          }}
                        >
                          {service.projects} PROJECTS
                        </Typography>
                      </GlassCard>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Grid>

          {/* Right side - Introduction */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Fira Code, monospace',
                  fontSize: '0.85rem',
                  color: '#44D482',
                  letterSpacing: '0.1em',
                  mb: 2,
                }}
              >
                {/* INTRODUCTION */}
                {'// INTRODUCTION'}
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: '#fff',
                  mb: 3,
                  lineHeight: 1.1,
                }}
              >
                HEY, I AM{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #44D482 0%, #6EEAA3 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  ABHISHEK KUMAR
                </Box>
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontWeight: 400,
                  fontStyle: 'italic',
                  mb: 4,
                  borderLeft: '3px solid #44D482',
                  pl: 3,
                }}
              >
                "Before software can be reusable it first has to be usable."
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                }}
              >
                I am a Software Engineer with experience in delivering appropriate technology solutions. 
                Comprehensive knowledge of platform development, enterprise architecture, agile methodologies, 
                cloud services, and web-based applications.
              </Typography>

              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  mt: 3,
                }}
              >
                Innovative change agent with a unique mix of high-level technology direction and deep 
                technical expertise, including published research paper in the field of Machine Learning.
              </Typography>

              {/* Code-style signature */}
              <Box
                sx={{
                  mt: 5,
                  p: 3,
                  background: 'rgba(26, 26, 36, 0.6)',
                  borderRadius: '12px',
                  border: '1px solid rgba(68, 212, 130, 0.15)',
                  fontFamily: 'Fira Code, monospace',
                  fontSize: '0.85rem',
                }}
              >
                <Box sx={{ color: 'rgba(255, 255, 255, 0.4)' }}>
                  {'// Current focus'}
                </Box>
                <Box sx={{ color: '#44D482', mt: 1 }}>
                  const <span style={{ color: '#fff' }}>focus</span> = [
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', pl: 3 }}>
                  "Full Stack Development",
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', pl: 3 }}>
                  "Cloud Architecture",
                </Box>
                <Box sx={{ color: 'rgba(255, 255, 255, 0.7)', pl: 3 }}>
                  "Machine Learning"
                </Box>
                <Box sx={{ color: '#44D482' }}>
                  ];
                </Box>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
